import { useMemo } from "react"
import { useGetTextTokens, TextTokenEntry } from "#/lib/contentful/hooks/useGetTextTokens"
import { useDoesUserSatisfyConditions } from "#/services/contentful/conditional-gate"
import { toCondition, RawContentfulConditionSkeleton } from "#/lib/contentful/utils/conditionUtils"
import { EntryWithoutUnresolvableLinks } from "#/utils/ContentTypes"

export type TextTokenLookup = (name: string) => string | undefined

export function useTextTokenLookup(): { lookup: TextTokenLookup; isLoading: boolean } {
  const { data: tokens, isLoading: isLoadingTokens } = useGetTextTokens()
  const { doesUserSatisfyConditions, isLoading: isLoadingConditions } = useDoesUserSatisfyConditions()

  const isLoading = isLoadingTokens || isLoadingConditions

  const tokensByName = useMemo(() => {
    const map = new Map<string, TextTokenEntry[]>()
    if (!tokens) return map
    for (const token of tokens) {
      if (!token.fields) continue
      const name = token.fields.name
      if (!name) continue
      const existing = map.get(name)
      if (existing) {
        existing.push(token)
      } else {
        map.set(name, [token])
      }
    }
    return map
  }, [tokens])

  const lookup: TextTokenLookup = useMemo(() => {
    return (name: string) => {
      const candidates = tokensByName.get(name)
      if (!candidates || candidates.length === 0) return undefined

      let defaultValue: string | undefined
      for (const token of candidates) {
        const conditionEntries = token.fields.conditions as
          | Array<EntryWithoutUnresolvableLinks<RawContentfulConditionSkeleton>>
          | undefined
        if (!conditionEntries || conditionEntries.length === 0) {
          if (defaultValue === undefined) defaultValue = token.fields.value ?? ""
          continue
        }
        try {
          const conditions = conditionEntries.map(toCondition).filter((c): c is NonNullable<typeof c> => c !== null)
          if (conditions.length === conditionEntries.length && doesUserSatisfyConditions(conditions)) {
            return token.fields.value ?? ""
          }
        } catch {
          continue
        }
      }
      return defaultValue ?? ""
    }
  }, [tokensByName, doesUserSatisfyConditions])

  return { lookup, isLoading }
}
